import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api, { fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

import { ClockWidget, timeOf, statusStyle } from "@/components/ClockWidget";
import { SignatureCard } from "@/components/SignatureCard";

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs font-semibold tracking-wider uppercase text-slate-400">{label}</div>
    <div className="text-sm text-slate-800 mt-1">{value || "—"}</div>
  </div>
);

export default function Profile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cr, setCr] = useState({ field: "address", label: "Address", new_value: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [att, setAtt] = useState([]);

  const load = () => api.get("/me/profile").then(({ data }) => setData(data)).catch(() => {});
  const loadAtt = () => api.get("/attendance/me").then(({ data }) => setAtt(data)).catch(() => {});
  useEffect(() => {
    load();
    loadAtt();
    api.get("/auth/sessions").then(({ data }) => setSessions(data)).catch(() => {});
    api.get("/auth/login-history").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-400 animate-pulse">Loading profile…</div>;
  const e = data.employee || {};

  const submitCr = async () => {
    try {
      await api.post("/me/change-request", cr);
      toast.success("Change request submitted for HR approval");
      setCr({ ...cr, new_value: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const changePw = async () => {
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="My Profile" subtitle={`${e.employee_no || ""} · ${user?.position || ""}`} />

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-5 flex items-center gap-5">
        {e.photo_url ? (
          <img src={e.photo_url} alt={`${e.first_name || ""} ${e.last_name || ""}`} className="h-20 w-20 rounded-full object-cover shrink-0" data-testid="profile-avatar" />
        ) : (
          <div className="h-20 w-20 rounded-full bg-blue-600 text-white flex items-center justify-center text-2xl font-semibold shrink-0" data-testid="profile-avatar">
            {(e.first_name || "U")[0]}{(e.last_name || "")[0]}
          </div>
        )}
        <div>
          <div className="text-xl font-semibold text-slate-900">{e.first_name} {e.last_name}</div>
          <div className="text-slate-500 text-sm">{e.position} · {e.department}</div>
          <div className="flex gap-2 mt-2">
            <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">{e.employment_status}</Badge>
            <Badge variant="secondary">{e.employment_type}</Badge>
            <Badge variant="outline">{user?.role}</Badge>
          </div>
        </div>
      </div>

      <Tabs defaultValue="personal">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="personal" data-testid="profile-tab-personal">Personal</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="profile-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
          <TabsTrigger value="government">Government & Bank</TabsTrigger>
          <TabsTrigger value="security" data-testid="profile-tab-security">Security</TabsTrigger>
          <TabsTrigger value="requests" data-testid="profile-tab-requests">Change Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="personal">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-5">
            <Field label="First Name" value={e.first_name} />
            <Field label="Middle Name" value={e.middle_name} />
            <Field label="Last Name" value={e.last_name} />
            <Field label="Preferred Name" value={e.preferred_name} />
            <Field label="Birth Date" value={fmtDate(e.birth_date)} />
            <Field label="Gender" value={e.gender} />
            <Field label="Civil Status" value={e.civil_status} />
            <Field label="Biometric ID" value={e.biometric_id} />
          </div>
        </TabsContent>

        <TabsContent value="attendance">
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-1"><ClockWidget onClocked={loadAtt} /></div>
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
              <div className="text-sm font-medium text-slate-800 mb-3">Recent Attendance</div>
              <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="profile-attendance-history">
                {att.slice(0, 15).map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                    <span className="text-slate-700 w-24">{fmtDate(h.date)}</span>
                    <span className="text-xs text-slate-500 font-mono flex-1">{timeOf(h.clock_in)} → {timeOf(h.clock_out)}</span>
                    {h.site_name && <span className="text-[11px] text-slate-400 hidden sm:inline mr-2">{h.site_name}</span>}
                    <Badge className={`${statusStyle[h.status] || "bg-slate-100 text-slate-600"} hover:opacity-100 text-[10px]`}>{h.status}{h.late_minutes ? ` ${h.late_minutes}m` : ""}</Badge>
                  </div>
                ))}
                {att.length === 0 && <div className="text-sm text-slate-400">No attendance records yet.</div>}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="employment">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-5">
            <Field label="Employee No" value={e.employee_no} />
            <Field label="Position" value={e.position} />
            <Field label="Job Level" value={e.job_level} />
            <Field label="Department" value={e.department} />
            <Field label="Employment Type" value={e.employment_type} />
            <Field label="Status" value={e.employment_status} />
            <Field label="Date Hired" value={fmtDate(e.date_hired)} />
            <Field label="Date Regularized" value={fmtDate(e.date_regularized)} />
            <Field label="Cost Center" value={e.cost_center} />
            <Field label="Work Location" value={e.work_location} />
          </div>
        </TabsContent>

        <TabsContent value="contact">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Email" value={e.email} />
              <Field label="Mobile" value={e.mobile} />
              <Field label="Address" value={e.address} />
            </div>
            <div className="border-t pt-5 grid sm:grid-cols-3 gap-5">
              <Field label="Emergency Contact" value={e.emergency_contact?.name} />
              <Field label="Relation" value={e.emergency_contact?.relation} />
              <Field label="Emergency Mobile" value={e.emergency_contact?.mobile} />
            </div>
            <div className="border-t pt-5">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="request-change-button"><Icons.FileEdit className="h-4 w-4 mr-1.5" /> Request Information Change</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Request Change</DialogTitle></DialogHeader>
                  <p className="text-sm text-slate-500">Sensitive master data changes require HR approval before taking effect.</p>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Field</Label>
                      <select className="w-full border rounded-lg h-10 px-3 text-sm" value={cr.field}
                        onChange={(ev) => {
                          const map = { address: "Address", mobile: "Mobile Number", email: "Email" };
                          setCr({ ...cr, field: ev.target.value, label: map[ev.target.value] });
                        }} data-testid="change-field-select">
                        <option value="address">Address</option>
                        <option value="mobile">Mobile Number</option>
                        <option value="email">Email</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>New Value</Label>
                      <Input value={cr.new_value} onChange={(ev) => setCr({ ...cr, new_value: ev.target.value })} data-testid="change-value-input" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={submitCr} disabled={!cr.new_value} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-change-request">Submit for Approval</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="government">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid sm:grid-cols-3 gap-5">
            <Field label="TIN" value={e.tax?.tin} />
            <Field label="SSS" value={e.government?.sss} />
            <Field label="Bank" value={e.bank?.bank_name} />
            <Field label="Account No" value={e.bank?.account_no} />
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="text-sm font-medium text-slate-800 mb-4">Change Password</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input type="password" value={pw.current_password} onChange={(ev) => setPw({ ...pw, current_password: ev.target.value })} data-testid="current-password-input" />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input type="password" value={pw.new_password} onChange={(ev) => setPw({ ...pw, new_password: ev.target.value })} data-testid="new-password-input" />
                </div>
                <Button onClick={changePw} className="bg-blue-600 hover:bg-blue-700" data-testid="change-password-button">Update Password</Button>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="text-sm font-medium text-slate-800 mb-4">Active Sessions</div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {sessions.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 text-sm">
                    <Icons.Monitor className="h-4 w-4 text-slate-400" />
                    <div className="flex-1 truncate">
                      <div className="text-slate-700 truncate">{s.device?.slice(0, 40) || "Unknown device"}</div>
                      <div className="text-xs text-slate-400">{s.ip} · {fmtDateTime(s.created_at)}</div>
                    </div>
                    {s.active && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px]">Active</Badge>}
                  </div>
                ))}
              </div>
            </div>
            <SignatureCard />
            <div className="bg-white rounded-xl border border-slate-200 p-6 lg:col-span-2">
              <div className="text-sm font-medium text-slate-800 mb-4">Login History</div>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                    <span className="text-slate-600">{fmtDateTime(h.at)}</span>
                    <span className="text-slate-400 text-xs">{h.ip}</span>
                    <Badge variant="secondary" className="text-[10px]">{h.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="requests">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {(data.change_requests || []).length === 0 && <div className="p-8 text-center text-sm text-slate-400">No change requests.</div>}
            {(data.change_requests || []).map((r) => (
              <div key={r.id} className="px-5 py-4 border-b border-slate-100 flex items-center justify-between" data-testid="change-request-row">
                <div>
                  <div className="text-sm font-medium text-slate-800">{r.label}</div>
                  <div className="text-xs text-slate-500">{r.old_value || "—"} → {r.new_value}</div>
                </div>
                <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50">{r.status}</Badge>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
